import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, AccordionDetails, AccordionSummary, Box, styled, Typography } from '@mui/material';

import { useGetAccessToken } from 'hooks/useGetAccessToken';

import { useDataContext } from 'components/OnePageTemplates/context';

import { hasMeaningfulValues } from 'helpers/hasMeaningfulValues';
import ToastService from 'helpers/ToastService';

import styles from './TeamSectionMobile.module.scss';

import colors from 'styles/sass-variables.module.scss';

const StyledAccordion = styled(Accordion)(() => ({
  boxShadow: 'none',
  borderRadius: '16px !important',

  backgroundColor: colors.op_second_green_100,

  '&.Mui-expanded': {
    minHeight: 0,
    backgroundColor: colors.white,
  },

  '&:before': {
    display: 'none',
  },

  '&:not(:last-child)': {
    marginBottom: '16px',
  },
}));

const StyledSummary = styled(AccordionSummary)(() => ({
  padding: 0,

  '&.Mui-expanded': {
    minHeight: 0,
  },

  '& .MuiAccordionSummary-content': {
    margin: 0,

    '&.Mui-expanded': {
      margin: 0,
    },
  },
}));

const StyledDetails = styled(AccordionDetails)(() => ({
  padding: '8px 24px 24px',
}));

export const TeamSectionMobile: FC = () => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const { data } = useDataContext();
  const { t } = useTranslation();

  const { teamMembers } = data;

  const accessToken = useGetAccessToken();

  const preloadImage = async (imageUrl: string, headers: Headers) => {
    try {
      const response = await fetch(imageUrl, { headers, redirect: 'follow' });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      return blob;
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }

      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading image:', error);
      }

      return null;
    }
  };

  const [imageLinks, setImageLinks] = useState<string[]>([]);
  useEffect(() => {
    const loadImages = async () => {
      const imagesToLoad = teamMembers!.map((member) => (typeof member.photoSrc === 'string' ? member.photoSrc : ''));

      const loadedImages = await Promise.all(
        imagesToLoad.map(async (imageUrl) => {
          if (!imageUrl) {
            return '';
          }

          const headers = new Headers();
          if (accessToken) {
            headers.append('Authorization', `${accessToken}`);
          }

          const blob = await preloadImage(imageUrl, headers);
          if (blob) {
            try {
              const objectURL = URL.createObjectURL(blob);
              return objectURL;
            } catch (error) {
              console.error(`Error creating object URL for ${imageUrl}`, error);
              return '';
            }
          } else {
            return '';
          }
        }),
      );

      setImageLinks(loadedImages);
    };

    loadImages();

    // Cleanup function to revoke object URLs
    return () => {
      imageLinks.forEach((link) => {
        if (link) {
          URL.revokeObjectURL(link);
        }
      });
    };
  }, [accessToken, teamMembers]);

  if (!hasMeaningfulValues({ teamMembers }) || !teamMembers) {
    return null;
  }

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box>
      {teamMembers.map(
        ({ position, fullName, dateOfBirth, education, workExperience, keySkills, achievements }, index) => (
          <StyledAccordion
            key={index}
            expanded={expanded === `panel${index + 1}`}
            onChange={handleChange(`panel${index + 1}`)}
          >
            <StyledSummary>
              <Box className={styles.summaryWrapper}>
                {imageLinks.length > 0 && imageLinks[index] && (
                  <img src={imageLinks[index]} alt={t('onePageMapping.teamMemberPhoto')} className={styles.img} />
                )}
                <Box className={styles.summaryContent}>
                  {fullName && (
                    <Typography variant="h4" component="p">
                      {fullName}
                    </Typography>
                  )}
                  {position && (
                    <Typography variant="body1" component="p">
                      {position}
                    </Typography>
                  )}
                </Box>
              </Box>
            </StyledSummary>
            <StyledDetails>
              <Box className={styles.detailsWrapper}>
                {dateOfBirth && (
                  <Box className={styles.detail}>
                    <Typography variant="h4" component="p" className={styles.detailTitle}>
                      {t('onePageMapping.dateBirth')}
                    </Typography>
                    <Typography variant="body1" component="p">
                      {dateOfBirth}
                    </Typography>
                  </Box>
                )}
                {education && (
                  <Box className={styles.detail}>
                    <Typography variant="h4" component="p" className={styles.detailTitle}>
                      {t('management.team.education')}
                    </Typography>
                    <Typography variant="body1" component="p">
                      {education}
                    </Typography>
                  </Box>
                )}
                {workExperience && (
                  <Box className={styles.detail}>
                    <Typography variant="h4" component="p" className={styles.detailTitle}>
                      {t('management.team.workExperience')}
                    </Typography>
                    <Typography variant="body1" component="p">
                      {workExperience}
                    </Typography>
                  </Box>
                )}
                {keySkills && (
                  <Box className={styles.detail}>
                    <Typography variant="h4" component="p" className={styles.detailTitle}>
                      {t('management.team.keySkills')}
                    </Typography>
                    <Typography variant="body1" component="p">
                      {keySkills}
                    </Typography>
                  </Box>
                )}
                {achievements && (
                  <Box className={styles.detail}>
                    <Typography variant="h4" component="p" className={styles.detailTitle}>
                      {t('onePageMapping.achievements')}
                    </Typography>
                    <Typography variant="body1" component="p">
                      {achievements}
                    </Typography>
                  </Box>
                )}
              </Box>
            </StyledDetails>
          </StyledAccordion>
        ),
      )}
    </Box>
  );
};
